import { useState, useEffect } from 'react';
import { Tournament, Team, ValidationError, DEFAULT_TOURNAMENT_RULES } from '@/types';
import { createTournament, updateTournament, getTournamentParticipants, setTournamentParticipants, canRemoveTeamFromTournament } from '@/lib/supabase';
import { v4 as uuidv4 } from 'uuid';

interface TournamentFormProps {
  tournament: Tournament | null;
  teams: Team[];
  onSuccess: () => void;
  onCancel: () => void;
}

export default function TournamentForm({ tournament, teams, onSuccess, onCancel }: TournamentFormProps) {
  const [name, setName] = useState(tournament?.name || '');
  const [date, setDate] = useState(tournament?.date || new Date().toISOString().split('T')[0]);
  const [venue, setVenue] = useState(tournament?.venue || '');
  const [leagueWinningPoint, setLeagueWinningPoint] = useState(tournament?.leagueWinningPoint || DEFAULT_TOURNAMENT_RULES.leagueWinningPoint);
  const [leagueGoldenPoint, setLeagueGoldenPoint] = useState(tournament?.leagueGoldenPoint || DEFAULT_TOURNAMENT_RULES.leagueGoldenPoint);
  const [playoffWinningPoint, setPlayoffWinningPoint] = useState(tournament?.playoffWinningPoint || DEFAULT_TOURNAMENT_RULES.playoffWinningPoint);
  const [playoffGoldenPoint, setPlayoffGoldenPoint] = useState(tournament?.playoffGoldenPoint || DEFAULT_TOURNAMENT_RULES.playoffGoldenPoint);
  const [selectedTeamIds, setSelectedTeamIds] = useState<Set<string>>(new Set());
  const [lockedTeamIds, setLockedTeamIds] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingParticipants, setLoadingParticipants] = useState(false);

  const isEditing = !!tournament;

  // Load existing participants when editing
  useEffect(() => {
    if (isEditing && tournament) {
      setLoadingParticipants(true);
      getTournamentParticipants(tournament.id)
        .then(async teamIds => {
          const teamIdSet = new Set(teamIds);
          setSelectedTeamIds(teamIdSet);

          // Check which teams cannot be removed (used in matches)
          const locked: string[] = [];
          for (const teamId of teamIds) {
            const canRemove = await canRemoveTeamFromTournament(tournament.id, teamId);
            if (!canRemove) {
              locked.push(teamId);
            }
          }
          setLockedTeamIds(new Set(locked));
        })
        .catch(console.error)
        .finally(() => setLoadingParticipants(false));
    }
  }, [isEditing, tournament]);

  const toggleTeam = (teamId: string) => {
    // If editing and team is locked (used in matches), don't allow removal
    if (isEditing && lockedTeamIds.has(teamId) && selectedTeamIds.has(teamId)) {
      setErrors([{
        id: uuidv4(),
        title: 'Cannot Remove Team',
        message: 'This team is already used in tournament matches and cannot be removed.',
      }]);
      return;
    }

    setSelectedTeamIds(prev => {
      const next = new Set(prev);
      if (next.has(teamId)) {
        next.delete(teamId);
      } else {
        next.add(teamId);
      }
      return next;
    });
    setErrors([]);
  };

  const selectAllTeams = () => {
    setSelectedTeamIds(new Set(teams.map(t => t.id)));
  };

  const clearAllTeams = () => {
    setSelectedTeamIds(new Set());
  };

  const validate = (): ValidationError[] => {
    const errs: ValidationError[] = [];

    if (!name.trim()) {
      errs.push({ id: uuidv4(), title: 'Name Required', message: 'Tournament name is required.' });
    }

    if (!date) {
      errs.push({ id: uuidv4(), title: 'Date Required', message: 'Tournament date is required.' });
    }

    if (selectedTeamIds.size < 2) {
      errs.push({ id: uuidv4(), title: 'Teams Required', message: 'Select at least 2 participating teams.' });
    }

    if (leagueWinningPoint <= 0) {
      errs.push({ id: uuidv4(), title: 'Invalid League Winning Point', message: 'League winning point must be greater than 0.' });
    }

    if (leagueGoldenPoint <= leagueWinningPoint) {
      errs.push({ id: uuidv4(), title: 'Invalid League Golden Point', message: 'League golden point must be greater than league winning point.' });
    }

    if (playoffWinningPoint <= 0) {
      errs.push({ id: uuidv4(), title: 'Invalid Playoff Winning Point', message: 'Playoff winning point must be greater than 0.' });
    }

    if (playoffGoldenPoint <= playoffWinningPoint) {
      errs.push({ id: uuidv4(), title: 'Invalid Playoff Golden Point', message: 'Playoff golden point must be greater than playoff winning point.' });
    }

    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validate();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    setErrors([]);

    try {
      const teamIdsArray = Array.from(selectedTeamIds);

      if (isEditing && tournament) {
        await updateTournament(tournament.id, {
          name: name.trim(),
          date,
          venue: venue.trim() || undefined,
          leagueWinningPoint,
          leagueGoldenPoint,
          playoffWinningPoint,
          playoffGoldenPoint,
        });
        await setTournamentParticipants(tournament.id, teamIdsArray);
      } else {
        const result = await createTournament({
          name: name.trim(),
          date,
          venue: venue.trim() || undefined,
          leagueWinningPoint,
          leagueGoldenPoint,
          playoffWinningPoint,
          playoffGoldenPoint,
        });
        await setTournamentParticipants(result.id, teamIdsArray);
      }
      onSuccess();
    } catch (err) {
      setErrors([{
        id: uuidv4(),
        title: 'Failed to Save',
        message: 'Could not save tournament. Please try again.',
      }]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-border rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="px-4 py-4 border-b border-border">
            <h2 className="text-xl font-bold text-text-primary">
              {isEditing ? 'Edit Tournament' : 'Create Tournament'}
            </h2>
          </div>

          {/* Content */}
          <div className="px-4 py-4 space-y-4">
            {/* Tournament Name */}
            <div>
              <label className="block text-sm text-text-secondary mb-2">Tournament Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-field"
                placeholder="Enter tournament name"
              />
            </div>

            {/* Date */}
            <div>
              <label className="block text-sm text-text-secondary mb-2">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="input-field w-full"
              />
            </div>

            {/* Venue */}
            <div>
              <label className="block text-sm text-text-secondary mb-2">Venue (Optional)</label>
              <input
                type="text"
                value={venue}
                onChange={(e) => setVenue(e.target.value)}
                className="input-field"
                placeholder="Enter venue"
              />
            </div>

            {/* Participating Teams */}
            <div className="card bg-surface-raised">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider">
                  Participating Teams ({selectedTeamIds.size}/{teams.length})
                </h3>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={selectAllTeams}
                    className="text-xs text-primary hover:underline"
                  >
                    Select All
                  </button>
                  <span className="text-text-muted">|</span>
                  <button
                    type="button"
                    onClick={clearAllTeams}
                    className="text-xs text-text-muted hover:underline"
                  >
                    Clear
                  </button>
                </div>
              </div>

              {loadingParticipants ? (
                <div className="flex justify-center py-4">
                  <span className="w-6 h-6 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
                </div>
              ) : teams.length === 0 ? (
                <p className="text-sm text-text-muted py-2">No teams available. Add teams first.</p>
              ) : (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {teams.map(team => {
                    const isSelected = selectedTeamIds.has(team.id);
                    const isLocked = isEditing && lockedTeamIds.has(team.id);
                    return (
                      <label
                        key={team.id}
                        className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
                          isSelected
                            ? 'bg-primary/10 border border-primary/30'
                            : 'hover:bg-surface'
                        } ${isLocked ? 'cursor-default' : 'cursor-pointer'}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleTeam(team.id)}
                          disabled={isLocked && isSelected}
                          className="w-4 h-4 rounded border-border disabled:opacity-50"
                          style={{ accentColor: team.color }}
                        />
                        <span
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: team.color }}
                        />
                        <span className="text-sm text-text-primary flex-1">{team.name}</span>
                        {isLocked && (
                          <span className="text-[10px] text-warning flex items-center gap-1">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M12 2C9.243 2 7 4.243 7 7v3H6a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2v-8a2 2 0 00-2-2h-1V7c0-2.757-2.243-5-5-5zm0 2c1.654 0 3 1.346 3 3v3H9V7c0-1.654 1.346-3 3-3z"/>
                            </svg>
                            Played
                          </span>
                        )}
                        <span
                          className="text-xs font-semibold px-2 py-0.5 rounded"
                          style={{ backgroundColor: `${team.color}20`, color: team.color }}
                        >
                          {team.shortName}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
              {selectedTeamIds.size < 2 && (
                <p className="text-xs text-warning mt-2">Select at least 2 participating teams.</p>
              )}
            </div>

            {/* League Rules */}
            <div className="card bg-surface-raised">
              <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
                League Rules
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-text-secondary mb-2">Winning Point</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={leagueWinningPoint}
                    onChange={(e) => setLeagueWinningPoint(parseInt(e.target.value) || 0)}
                    className="input-field"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm text-text-secondary mb-2">Golden Point</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={leagueGoldenPoint}
                    onChange={(e) => setLeagueGoldenPoint(parseInt(e.target.value) || 0)}
                    className="input-field"
                    min="1"
                  />
                </div>
              </div>
              <p className="text-xs text-text-muted mt-2">
                Default: {DEFAULT_TOURNAMENT_RULES.leagueWinningPoint} winning, {DEFAULT_TOURNAMENT_RULES.leagueGoldenPoint} golden
              </p>
            </div>

            {/* Playoff Rules */}
            <div className="card bg-surface-raised">
              <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
                Playoff Rules
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-text-secondary mb-2">Winning Point</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={playoffWinningPoint}
                    onChange={(e) => setPlayoffWinningPoint(parseInt(e.target.value) || 0)}
                    className="input-field"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm text-text-secondary mb-2">Golden Point</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={playoffGoldenPoint}
                    onChange={(e) => setPlayoffGoldenPoint(parseInt(e.target.value) || 0)}
                    className="input-field"
                    min="1"
                  />
                </div>
              </div>
              <p className="text-xs text-text-muted mt-2">
                Default: {DEFAULT_TOURNAMENT_RULES.playoffWinningPoint} winning, {DEFAULT_TOURNAMENT_RULES.playoffGoldenPoint} golden
              </p>
            </div>

            {/* Errors */}
            {errors.length > 0 && (
              <div className="bg-error/20 border border-error rounded-lg p-3">
                {errors.map((err) => (
                  <p key={err.id} className="text-error text-sm">{err.message}</p>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="px-4 py-4 border-t border-border space-y-3">
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary bg-success text-white"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {isEditing ? 'SAVING...' : 'CREATING...'}
                </span>
              ) : (
                isEditing ? 'SAVE CHANGES' : 'CREATE TOURNAMENT'
              )}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="btn-secondary"
            >
              CANCEL
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
